import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = (studentId) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check current subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported || !studentId) return;
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };
    
    checkSubscription();
  }, [isSupported, studentId]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }
    
    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !studentId) {
      setError('Notifications non supportées ou utilisateur non connecté');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Permission de notification refusée');
        setLoading(false);
        return false;
      }
      
      // Register service worker
      await registerServiceWorker();
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID key
      let vapidKey = VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        const response = await axios.get(`${API}/push/vapid-key`);
        vapidKey = response.data.publicKey;
      }
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      
      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON(),
        student_id: studentId
      });
      
      setIsSubscribed(true);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError(err.message || 'Erreur lors de l\'inscription aux notifications');
      setLoading(false);
      return false;
    }
  }, [isSupported, studentId, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported || !studentId) return false;
    
    setLoading(true);
    setError(null);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      // Notify backend
      const formData = new FormData();
      formData.append('student_id', studentId);
      await axios.post(`${API}/push/unsubscribe`, formData);
      
      setIsSubscribed(false);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Error unsubscribing from push:', err);
      setError(err.message || 'Erreur lors de la désinscription');
      setLoading(false);
      return false;
    }
  }, [isSupported, studentId]);

  // Test notification
  const sendTestNotification = useCallback(async () => {
    if (!studentId) return;
    
    try {
      const formData = new FormData();
      formData.append('student_id', studentId);
      const response = await axios.post(`${API}/push/test`, formData);
      return response.data;
    } catch (err) {
      console.error('Error sending test notification:', err);
      throw err;
    }
  }, [studentId]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
};

export default usePushNotifications;
